import { useEffect, useState } from 'react'
import { LucideIcon, Dot, ChevronRight } from 'lucide-react'
import { NavLink, useMatches } from 'react-router-dom'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { SidebarHeaderIcon } from '@/components/sidebar/sidebarHeaderIcon'
import { useRole } from '@/hooks/useRole'
import { DocsDropdown } from '@/components/sidebar/docsDropdown'
import { AppInfo } from '@/components/sidebar/appInfo'
import { ApiDocLink } from './apiDocLink'

export enum NavRolesOperator {
  and = 'AND',
  or = 'OR',
}

export type Nav = NavGroupProps[]
type NavGroupProps = {
  title?: string
  items: (NavItemDropdownProps | NavItemProps)[]
}
type NavItemProps = {
  title: string
  url: string
  roles?: string[] | string
  rolesOperator?: NavRolesOperator
  Icon: LucideIcon
  isEnd?: boolean
}
type NavItemDropdownProps = {
  title: string
  Icon: LucideIcon
  items: NavSubItemProps[]
}
type NavSubItemProps = {
  title: string
  url: string
  roles?: string[] | string
  rolesOperator?: NavRolesOperator
  color: string
}

type AppSidebarProps = {
  nav: Nav
}

export const AppSidebar = ({ nav }: AppSidebarProps) => {
  const { setOpenMobile } = useSidebar()
  const { hasAccess, hasAnyAccess } = useRole()

  const accessibleGroups = nav
    .filter((group) =>
      group.items.some((item) =>
        !('items' in item)
          ? !item.roles ||
            (item.roles &&
              (item.rolesOperator && item.rolesOperator === NavRolesOperator.or ? hasAnyAccess(item.roles) : hasAccess(item.roles)))
          : item.items.some(
              (subItem) =>
                subItem.roles && (subItem.rolesOperator === NavRolesOperator.or ? hasAnyAccess(subItem.roles) : hasAccess(subItem.roles)),
            ),
      ),
    )
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) =>
          !('items' in item)
            ? !item.roles ||
              (item.roles &&
                (item.rolesOperator && item.rolesOperator === NavRolesOperator.or ? hasAnyAccess(item.roles) : hasAccess(item.roles)))
            : item.items.some(
                (subItem) =>
                  subItem.roles && (subItem.rolesOperator === NavRolesOperator.or ? hasAnyAccess(subItem.roles) : hasAccess(subItem.roles)),
              ),
        )
        .map((item): NavItemDropdownProps | NavItemProps => {
          if ('items' in item) {
            return {
              ...item,
              items: item.items.filter(
                (subItem) =>
                  subItem.roles && (subItem.rolesOperator === NavRolesOperator.or ? hasAnyAccess(subItem.roles) : hasAccess(subItem.roles)),
              ),
            }
          }
          return { ...item }
        }),
    }))

  return (
    <Sidebar variant="sidebar">
      <SidebarHeader className="p-0 gap-0">
        <div className="py-3 border-b">
          <img src="/logo.svg" alt="Logo" className="mx-auto h-[28px]" />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarHeaderIcon />
        {accessibleGroups.map((group) => (
          <SidebarGroup key={!group.title ? group.items[0].title : group.title}>
            {group.title && <SidebarGroupLabel>{group.title}</SidebarGroupLabel>}
            <SidebarMenu>
              {group.items &&
                group.items.map((item) =>
                  'items' in item ? (
                    <CollapsibleGroup key={item.title} item={item} />
                  ) : (
                    <SidebarMenuItem key={item.title}>
                      <NavLink to={item.url} end={item.isEnd} onClick={() => setOpenMobile(false)}>
                        {({ isActive }) => (
                          <SidebarMenuButton isActive={isActive}>
                            <item.Icon />
                            <span>{item.title}</span>
                          </SidebarMenuButton>
                        )}
                      </NavLink>
                    </SidebarMenuItem>
                  ),
                )}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>
      {hasAccess('document-read') && (
        <SidebarFooter className="flex-row justify-around p-4">
          <AppInfo />
          <DocsDropdown />
          <ApiDocLink />
        </SidebarFooter>
      )}
    </Sidebar>
  )
}

const CollapsibleGroup = ({ item }: { item: NavItemDropdownProps }) => {
  const { setOpenMobile } = useSidebar()
  const matches = useMatches()
  const isActive = item.items.some((subItem) =>
    matches.some((match) => match.pathname === new URL(subItem.url, window.location.href).pathname),
  )
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setOpen(isActive)
  }, [isActive])

  return (
    <Collapsible className="group/collapsible" open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <SidebarMenuButton>
          {item.Icon && <item.Icon />}
          <span>{item.title}</span>
          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
        </SidebarMenuButton>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <SidebarGroupContent>
          {item.items.map((subItem) => (
            <SidebarMenuSub key={subItem.title}>
              <SidebarMenuSubItem>
                <NavLink to={subItem.url} onClick={() => setOpenMobile(false)}>
                  {({ isActive }) => (
                    <SidebarMenuSubButton isActive={isActive}>
                      <Dot className="!size-8 inline" style={{ color: subItem.color }} />
                      <span>{subItem.title}</span>
                    </SidebarMenuSubButton>
                  )}
                </NavLink>
              </SidebarMenuSubItem>
            </SidebarMenuSub>
          ))}
        </SidebarGroupContent>
      </CollapsibleContent>
    </Collapsible>
  )
}
